// src/pages/Dashboard.js

import React, { useEffect, useState, useMemo, useContext } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  CircularProgress
} from '@mui/material';
import {
  AttachMoney,
  Group,
  Storage,
  AccountBalance
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import Papa from 'papaparse';
import Plot from 'react-plotly.js';

import { AuthContext } from '../context/AuthContext';

// Helper: extract city and state from an address
const extractCityState = (address) => {
  if (!address || typeof address !== 'string')
    return 'Unknown City, Unknown State';
  const parts = address.split(',');
  if (parts.length < 2) return address;
  const city = parts[parts.length - 2].trim() || 'Unknown City';
  const stateZip = parts[parts.length - 1].trim() || 'Unknown State';
  const state = stateZip.split(' ')[0];
  return `${city}, ${state}`;
};

// Helper: pick a bar color by program type
const getBarColor = (programType) => {
  switch (programType) {
    case 'REACH': return '#8884d8';
    case 'KCC':   return '#82ca9d';
    case 'MSSP':  return 'gray';
    default:      return '#ffc658';
  }
};

export default function Dashboard() {
  const { user } = useContext(AuthContext);
  const domain = user?.email.split('@')[1]?.toLowerCase() || '';
  const partnerFilter =
    domain === 'merchantsbonding.com' ? 'merchants' :
    domain === 'colonialsurety.com'  ? 'colonial'  :
    domain === 'everestglobal.com'      ? 'everest': '';

  // state
  const [accountsData, setAccountsData] = useState([]);
  const [chartData,    setChartData]    = useState([]);
  const [bonds,        setBonds]        = useState(0);
  const [partnerIDs,   setPartnerIDs]   = useState(new Set());
  const [subsidiaryMap,setSubsidiaryMap]= useState(new Map());
  const [loading,      setLoading]      = useState(true);

  // Load all CSVs in parallel whenever partnerFilter changes
  useEffect(() => {
    if (!partnerFilter) {
      setAccountsData([]);
      setChartData([]);
      setBonds(0);
      setPartnerIDs(new Set());
      setSubsidiaryMap(new Map());
      setLoading(false);
      return;
    }
    setLoading(true);

    // 1) Subsidiaries & Accounts
    const subsPromise = new Promise((resolve) => {
      Papa.parse('/data/ACO_subsidiary list.csv', {
        download: true, header: true, skipEmptyLines: true,
        complete: ({ data }) => {
          const rows = data.filter(r =>
            r.surety_access?.trim().toLowerCase() === partnerFilter &&
            r.ACO_ID && r.subsidiary_co && r.parent_co && r.address
          );

          // build IDs & map
          const ids = new Set(rows.map(r => r.ACO_ID.trim()));
          const map = new Map(rows.map(r => [r.ACO_ID.trim(), r.subsidiary_co.trim()]));

          // tableAccounts deduped by parent_co
          const seen = new Set();
          const tableAccounts = rows.filter(r => {
            const key = r.parent_co.trim().toLowerCase();
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          }).map(r => ({
            id:        r.ACO_ID.trim(),
            name:      r.parent_co.trim(),
            hqAddress: extractCityState(r.address),
            programType: (r.program_type||'').trim().toUpperCase()
          }));
          if (partnerFilter=== 'everest'){
            const desiredOrder = [
                'Upperline Health Inc.',
              'U.S. Medical Management, LLC',
              'US Renal Care, Inc.'
            ];
            tableAccounts.sort((a, b) => {
    const idxA = desiredOrder.indexOf(a.name);
    const idxB = desiredOrder.indexOf(b.name);
    return (idxA === -1 ? 999 : idxA) - (idxB === -1 ? 999 : idxB);
  });
          }
          resolve({ ids, map, tableAccounts });
        },
        error: () => resolve({ ids:new Set(), map:new Map(), tableAccounts:[] })
      });
    });

    // 2) Bonds count
    const bondsPromise = new Promise((resolve) => {
      Papa.parse('/data/summary_page.csv', {
        download: true, header: true, skipEmptyLines: true,
        complete: ({ data }) => {
          const count = data.filter(r =>
            r.surety_access?.trim().toLowerCase() === partnerFilter
          ).length;
          resolve(count);
        },
        error: () => resolve(0)
      });
    });

    // 3) Savings data
    const savingsPromise = new Promise((resolve) => {
      Papa.parse('/data/shared_savings.csv', {
        download: true, header: true,
        complete: ({ data }) => {
          const parsed = data.map(item => ({
            id:           (item.ID||'').trim(),
            savings:      parseFloat((item.Savings_Gross||'').replace(/,/g,'')) || 0,
            membership:   parseFloat((item.Membership||'').replace(/,/g,''))   || 0,
            program_type: (item.Type||'').trim().toUpperCase()
          }));
          resolve(parsed);
        },
        error: () => resolve([])
      });
    });

    Promise.all([subsPromise, bondsPromise, savingsPromise])
      .then(([subsRes, bondCount, savingsRes]) => {
        setPartnerIDs(subsRes.ids);
        setSubsidiaryMap(subsRes.map);
        setAccountsData(subsRes.tableAccounts);
        setBonds(bondCount);
        setChartData(savingsRes);
      })
      .finally(() => setLoading(false));

  }, [partnerFilter]);

  // Merge & filter chart data for this partner
  const mergedData = useMemo(() => {
    return chartData
      .filter(c => partnerIDs.has(c.id))
      .map(c => ({
        name:        subsidiaryMap.get(c.id),
        savings:     c.savings,
        membership:  c.membership,
        program_type:c.program_type
      }));
  }, [chartData, partnerIDs, subsidiaryMap]);

  // Stats
  const totalSavings = useMemo(() =>
    Math.round(mergedData.reduce((sum, d) => sum + d.savings, 0))
      .toLocaleString()
  , [mergedData]);

  const totalMembership = useMemo(() =>
    mergedData.reduce((sum, d) => sum + d.membership, 0)
      .toLocaleString()
  , [mergedData]);

  const totalAccounts = accountsData.length;

  const accountStats = [
    { title: 'Estimated Savings', value: `$${totalSavings}`,      icon: <AttachMoney /> },
    { title: 'Membership',        value: totalMembership,         icon: <Group />       },
    { title: 'Accounts',          value: `${totalAccounts}`,      icon: <Storage />     },
    { title: 'Bonds',             value: `${bonds}`,              icon: <AccountBalance /> }
  ];

  // Prepare Plotly traces
  const grouped = useMemo(() => {
    const byType = {};
    mergedData.forEach(d => (byType[d.program_type] ||= []).push(d));
    Object.values(byType).forEach(arr => arr.sort((a,b) => b.savings - a.savings));
    return byType;
  }, [mergedData]);

  const desiredOrder = useMemo(() => ['REACH','KCC','MSSP', 'NON-CMS'], []);
  const yOrder = useMemo(() => {
    return desiredOrder.flatMap(pt => (grouped[pt]||[]).map(d => d.name));
  }, [grouped, desiredOrder]);

  const traces = useMemo(() => {
    return desiredOrder.flatMap(pt => {
      if (!(grouped[pt]||[]).length) return [];
      return [{
        type: 'bar',
        orientation: 'h',
        name: pt,
        x: yOrder.map(name => {
          const rec = mergedData.find(d => d.name === name);
          return rec?.program_type===pt ? rec.savings : null;
        }),
        y: yOrder,
        marker: { color: getBarColor(pt) },
        customdata: yOrder,
        hovertemplate: 'ACO: %{customdata}<br>Savings: $%{x:,.0f}<extra></extra>'
      }];
    });
  }, [grouped, yOrder, mergedData, desiredOrder]);

  // Render
  if (loading) {
    return (
      <Box sx={{
        width: '100%',
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', px: 4, py: 3 }}>
      <Typography variant='h5' fontWeight='bold' mb={3}>
        Accounts
      </Typography>

      {/* Stats Cards */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
        {accountStats.map((stat, i) => (
          <Card key={i} sx={{ flex: '1 1 250px' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar sx={{ bgcolor: '#6c5ce7', mr: 2 }}>{stat.icon}</Avatar>
              <Box>
                <Typography variant='subtitle2' color='textSecondary'>
                  {stat.title}
                </Typography>
                <Typography variant='h6' fontWeight='bold'>
                  {stat.value}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Savings Chart */}
      <Typography variant='h6' fontWeight='bold' mb={2}>
        Savings by ACO (Q2 2025)
      </Typography>
      <Paper sx={{ mb: 4 }}>
        <Plot
          data={traces}
          layout={{
            barmode: 'group',
            autosize: true,
            bargap: 0.1,
            xaxis: { title: 'Gross Savings $', tickprefix: '$' },
            yaxis: {
              showticklabels: false,
              categoryorder: 'array',
              categoryarray: yOrder
            },
            margin: { t: 20, r: 30, l: 50, b: 40 }
          }}
          useResizeHandler
          style={{ width: '100%', height: 400 }}
        />
      </Paper>

      {/* Accounts Table */}
      <Typography variant='h6' fontWeight='bold' mb={2}>
        Accounts
      </Typography>
      <TableContainer component={Paper}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>HQ Address</TableCell>
              <TableCell>Program Type</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {accountsData.map(acct => (
              <TableRow
                key={acct.id}
                component={Link}
                to={`/accounts/${acct.id}`}
                sx={{
                  textDecoration: 'none',
                  color: 'inherit',
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: '#f5f5f5' }
                }}
              >
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar
                      sx={{ bgcolor: '#e0e0e0', mr: 2, width: 30, height: 30 }}
                    >
                      {acct.name.charAt(0)}
                    </Avatar>
                    {acct.name}
                  </Box>
                </TableCell>
                <TableCell>{acct.hqAddress}</TableCell>
                <TableCell>{acct.programType}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
